import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Button,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  IconButton,
  Checkbox,
  Chip,
} from "@mui/material";
import {
  Search,
  KeyboardArrowDown,
  KeyboardArrowUp,
  CheckCircle,
  InsertDriveFile,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import shipmentService from "../../services/shipmentService";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import ConsolidateItemsDialog from "../../components/sailing/ConsolidateItemsDialog";
import AssignDockReceiptsDialog from "../../components/sailing/AssignDockReceiptsDialog";
import useUrlState from "../../hooks/useUrlState";
import { slugifyTab } from "../../utils/tabSlug";

const TABS = [
  { label: "All", value: "" },
  { label: "Pending Email", value: "pending" },
  { label: "Email Sent", value: "sent" },
];

// Module-level (not inline in the component) so useUrlState gets a stable
// reference every render — see that hook's own doc comment for why.
const URL_SCHEMA = {
  tab: { default: "" },
};

const AdminReadyToSail = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [shipments, setShipments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [urlState, setUrlState] = useUrlState(URL_SCHEMA);
  const tab = Math.max(0, TABS.findIndex((t) => slugifyTab(t.label) === urlState.tab));
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [expandedId, setExpandedId] = useState(null);
  const [consolidateOpen, setConsolidateOpen] = useState(false);
  const [assignDrOpen, setAssignDrOpen] = useState(false);

  // Item-level selection — keys are item._id, used by "Consolidate Items" /
  // "Assign Dock Receipts to Sailing". Each shipment's expanded row also
  // has a "select all in this shipment" checkbox that just toggles all of
  // that shipment's item ids in here.
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());

  const toggleItemSelected = (itemId) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  // Selects/deselects every item on one shipment at once — the whole point
  // being a shipment with 100+ items doesn't need to be checked off one by
  // one.
  const toggleShipmentItemsSelected = (shipment) => {
    const allSelected = shipment.items.every((it) => selectedItemIds.has(it._id));
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      shipment.items.forEach((it) => {
        if (allSelected) next.delete(it._id);
        else next.add(it._id);
      });
      return next;
    });
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await shipmentService.listReadyToSail({
        filter: TABS[tab].value || undefined,
        search: search || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setShipments(data.data.shipments || []);
      setTotal(data.data.total || 0);
    } catch (err) {
      enqueueSnackbar("Failed to load shipments", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearch(searchInput);
    }, 600);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, search, page, rowsPerPage]);

  // { shipmentId, itemId } pairs for every currently checked ITEM — the
  // input both dialogs need to preview/send their email.
  const selectedItemRefs = shipments.flatMap((s) =>
    (s.items || [])
      .filter((it) => selectedItemIds.has(it._id))
      .map((it) => ({ shipmentId: s._id, itemId: it._id })),
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Ready to Sail"
        subtitle="Received items with a dock receipt assigned that aren't on a sailing yet — select items and Consolidate Items or send an Authorization Email"
        action={
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              disabled={selectedItemIds.size === 0}
              onClick={() => setConsolidateOpen(true)}
            >
              Consolidate Items ({selectedItemIds.size})
            </Button>
            <Button
              variant="outlined"
              disabled={selectedItemIds.size === 0}
              onClick={() => setAssignDrOpen(true)}
            >
              Authorization Email ({selectedItemIds.size})
            </Button>
          </Box>
        }
      />

      <ConsolidateItemsDialog
        open={consolidateOpen}
        onClose={() => setConsolidateOpen(false)}
        items={selectedItemRefs}
        onSent={() => {
          setSelectedItemIds(new Set());
          load();
        }}
      />
      <AssignDockReceiptsDialog
        open={assignDrOpen}
        onClose={() => setAssignDrOpen(false)}
        items={selectedItemRefs}
        onSent={() => {
          setSelectedItemIds(new Set());
          load();
        }}
      />

      <Card>
        <Box
          sx={{
            px: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setUrlState({ tab: slugifyTab(TABS[v].label) });
              setPage(0);
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              maxWidth: "100%",
              "& .MuiTab-root": {
                fontSize: 13,
                textTransform: "none",
                fontWeight: 600,
                minHeight: 44,
              },
            }}
          >
            {TABS.map((t, i) => (
              <Tab key={i} label={t.label} />
            ))}
          </Tabs>
          <Box sx={{ pb: 1, width: { xs: "100%", sm: "auto" } }}>
            <TextField
              size="small"
              placeholder="Search tracking #, D/R #…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ width: { xs: "100%", sm: 260 } }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 16, color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        </Box>

        {loading ? (
          <SkeletonLoader rows={Math.min(8, rowsPerPage)} />
        ) : shipments.length === 0 ? (
          <EmptyState
            icon="🚢"
            title="Nothing ready to sail"
            description="Shipments show up here once every item on them has a dock receipt number assigned."
          />
        ) : (
          <>
            <Box sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Tracking #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell>Invoice Doc</TableCell>
                    <TableCell>Email Status</TableCell>
                    <TableCell align="center" sx={{ width: 56 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shipments.map((s) => {
                    const isExpanded = expandedId === s._id;
                    const allItemsSelected =
                      s.items.length > 0 && s.items.every((it) => selectedItemIds.has(it._id));
                    const someItemsSelected = s.items.some((it) => selectedItemIds.has(it._id));
                    return (
                      <React.Fragment key={s._id}>
                        <TableRow
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => setExpandedId(isExpanded ? null : s._id)}
                        >
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              size="small"
                              checked={allItemsSelected}
                              indeterminate={someItemsSelected && !allItemsSelected}
                              onChange={() => toggleShipmentItemsSelected(s)}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "monospace",
                              fontSize: 12,
                              color: "text.secondary",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {s.trackingNumber}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {s.customer?.firstName} {s.customer?.lastName}
                          </TableCell>
                          <TableCell>
                            {s.items.length}/{s.totalItemCount ?? s.items.length}
                          </TableCell>
                          <TableCell>
                            {s.invoiceDocuments.length > 0 ? (
                              <Chip
                                icon={<InsertDriveFile sx={{ fontSize: 14 }} />}
                                label={`${s.invoiceDocuments.length} file(s)`}
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <Chip label="No invoice uploaded" size="small" color="warning" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                              {s.dockReceiptEmailSentAt ? (
                                <Chip
                                  icon={<CheckCircle sx={{ fontSize: 14 }} />}
                                  label={`Consolidate — Sent ${dayjs(s.dockReceiptEmailSentAt).format("MMM D, h:mma")}`}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              ) : (
                                <Chip label="Consolidate — Not sent" size="small" variant="outlined" />
                              )}
                              {s.assignDrEmailSentAt ? (
                                <Chip
                                  icon={<CheckCircle sx={{ fontSize: 14 }} />}
                                  label={`Authorization — Sent ${dayjs(s.assignDrEmailSentAt).format("MMM D, h:mma")}`}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              ) : (
                                <Chip label="Authorization — Not sent" size="small" variant="outlined" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedId(isExpanded ? null : s._id);
                              }}
                            >
                              {isExpanded ? (
                                <KeyboardArrowUp fontSize="small" />
                              ) : (
                                <KeyboardArrowDown fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ py: 2, bgcolor: "action.hover" }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell padding="checkbox" />
                                    <TableCell>Description</TableCell>
                                    <TableCell>Tracking #</TableCell>
                                    <TableCell>Dock Receipt #</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {s.items.map((it) => (
                                    <TableRow key={it._id}>
                                      <TableCell padding="checkbox">
                                        <Checkbox
                                          size="small"
                                          checked={selectedItemIds.has(it._id)}
                                          onChange={() => toggleItemSelected(it._id)}
                                        />
                                      </TableCell>
                                      <TableCell>{it.description}</TableCell>
                                      <TableCell sx={{ fontFamily: "monospace" }}>
                                        {it.trackingNumber || "—"}
                                      </TableCell>
                                      <TableCell sx={{ fontFamily: "monospace" }}>
                                        {it.dockReceiptNumber}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(+e.target.value);
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{ borderTop: "1px solid", borderColor: "divider" }}
            />
          </>
        )}
      </Card>
    </Box>
  );
};

export default AdminReadyToSail;
